import type { DocumentContentType } from '@tandemone/types'
import axios from 'axios'
import { useState } from 'react'

import { extractErrorMessage } from '@/api/extract-error'
import { captureException } from '@/domains/observability'

import {
  completeDocumentUpload,
  requestDocumentUpload,
  uploadDocumentContent
} from './api'

export type DocumentUploadStatus =
  | 'idle'
  | 'requesting'
  | 'uploading'
  | 'completing'
  | 'done'
  | 'error'

export type UploadedDocument = {
  documentId: string
  fileName: string
  byteSize: number
  contentType: string
}

const NETWORK_BLOCKED_MESSAGE =
  "The upload couldn't reach our servers. Check your connection, or ask your IT team whether file uploads to platform.tandemone.net are blocked."

/* the route restricts contentType to a union of literals, so ajv reports one
 * failure per branch and the raw text reaches the user as "body/contentType
 * must be equal to constant" repeated five times. */
function uploadMessage(caught: unknown, file: File): string {
  if (axios.isAxiosError(caught) && !caught.response) {
    return NETWORK_BLOCKED_MESSAGE
  }
  const raw = extractErrorMessage(caught)
  if (raw.includes('body/contentType')) {
    return `${file.type || 'That file type'} is not supported. Use a PNG, JPEG, or WebP image, a PDF, or an MP4 video.`
  }
  return raw
}

export function useDocumentUpload() {
  const [status, setStatus] = useState<DocumentUploadStatus>('idle')
  const [error, setError] = useState<string | null>(null)

  async function upload(file: File): Promise<UploadedDocument> {
    setError(null)
    try {
      setStatus('requesting')
      const intent = await requestDocumentUpload({
        fileName: file.name,
        contentType: file.type as DocumentContentType,
        byteSize: file.size
      })

      /* through our origin, not intent.uploadUrl: corporate filters block the
       * storage host while allowing ours */
      setStatus('uploading')
      await uploadDocumentContent(intent.documentId, file)

      setStatus('completing')
      const completed = await completeDocumentUpload(intent.documentId)

      setStatus('done')
      return {
        documentId: completed.document.id,
        fileName: completed.document.fileName,
        byteSize: completed.document.byteSize,
        contentType: completed.document.contentType
      }
    } catch (caught) {
      setStatus('error')
      if (caught instanceof Error) captureException(caught)
      setError(uploadMessage(caught, file))
      throw caught
    }
  }

  function reset() {
    setStatus('idle')
    setError(null)
  }

  return { upload, status, error, reset }
}
